// =============================================================================
// Solicitar revisão do orçamento — dois caminhos
//
// Validado em 23/09:
//   Aumento ....... linha orçamentária, mês do orçamento, valor solicitado e
//                   justificativa (rota /api/revisoes, que já existia).
//   Remanejar ..... tirar de onde sobra e pôr onde falta, sem mudar o total:
//                   entre linhas (mesmo mês), entre meses (mesma linha) ou no
//                   ano (meses restantes). Barra de valor no padrão de ajuste
//                   de limite de cartão (rota /api/remanejamentos, do Enzo).
//
// ⚠️ Nos dois, o pedido NÃO altera o orçamento: vai para OPERACIONAL →
// Solicitações, e o Financeiro decide. A origem só cede o que ainda não foi
// realizado nem comprometido.
//
// Regras do remanejamento, respondidas pelo Financeiro em 23/09 (migration 045):
//   - só dentro do MESMO centro de custo;
//   - mês fechado CEDE valor (a sobra de agosto pode ir para outubro), mas não
//     recebe: o destino vai do mês corrente em diante;
//   - destino sem linha orçada no mês pode ser escolhido: a linha é criada
//     quando o Financeiro aprovar. Só entra categoria que recebe orçamento.
// =============================================================================

import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  buscarOrcadoRealizado,
  FalhaApi,
  solicitarRemanejamento,
  solicitarRevisao,
  type LinhaMes,
} from "../lib/api.ts";
import { moeda } from "../lib/formato.ts";
import { Modal } from "../componentes/Modal.tsx";
import { CampoDinheiro } from "../componentes/CampoDinheiro.tsx";
import { Vazio } from "../componentes/ui.tsx";

type Cadastros = { categorias: { id: string; codigo: string | null; nome: string; natureza: string }[] };

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const comp = (ano: number, m: number) => `${ano}-${String(m + 1).padStart(2, "0")}-01`;
type Modo = "linhas" | "meses" | "ano";
type Cat = { chave: string; ccId: string; ccRotulo: string; catId: string; catRotulo: string; orc: bigint[]; usado: bigint[] };

const rotuloCampo = "text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase";
const campo = "mt-1 w-full rounded-lg border border-linha bg-superficie px-2.5 py-2 text-[13px]";

export function ModalRevisao({
  ano,
  centroCustoId,
  onFechar,
}: {
  ano: number;
  centroCustoId: string;
  onFechar: () => void;
}) {
  const [caminho, setCaminho] = useState<"aumento" | "remanejar">("aumento");
  const [aviso, setAviso] = useState<string | null>(null);

  // Meses que podem RECEBER: do corrente em diante, dentro do exercício. Os que
  // podem CEDER são todos os do exercício — mês fechado cede valor (045).
  const hoje = new Date();
  const mesesAbertos = ano < hoje.getFullYear() ? []
    : ano > hoje.getFullYear() ? MESES.map((_, i) => i)
    : MESES.map((_, i) => i).filter((i) => i >= hoje.getMonth());
  const mesesTodos = MESES.map((_, i) => i);

  return (
    <Modal titulo="Solicitar revisão do orçamento" onFechar={onFechar} largura="max-w-2xl">
      {aviso ? (
        <div>
          <p className="rounded-lg border border-ok-linha bg-ok-bg px-3 py-2.5 text-[13px] text-ok-tinta">{aviso}</p>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onFechar} className="rounded-lg bg-azul px-4 py-2 text-[13px] font-bold text-white">
              Fechar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-[13px] leading-snug text-tinta-media">
            O pedido vai para o Financeiro e <strong className="font-semibold">não altera o orçamento</strong> até
            ser aprovado. A decisão fica no Histórico.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {([
              ["aumento", "Solicitar aumento", "O teto de uma linha num mês não comporta o gasto"],
              ["remanejar", "Remanejar", "Tirar de onde sobra e pôr onde falta, sem mudar o total"],
            ] as const).map(([v, t, d]) => (
              <button key={v} type="button" aria-pressed={caminho === v} onClick={() => setCaminho(v)}
                className={`rounded-[10px] border-[1.5px] px-3 py-2.5 text-left ${caminho === v ? "border-laranja bg-[#fff8ef]" : "border-linha bg-superficie hover:border-linha-forte"}`}>
                <span className="block text-[13px] font-bold">{t}</span>
                <span className="block text-[11.5px] leading-snug text-tinta-fraca">{d}</span>
              </button>
            ))}
          </div>

          {mesesAbertos.length === 0 ? (
            <Vazio titulo={`O exercício ${ano} já passou`} dica="O aumento e o destino do remanejamento valem do mês corrente em diante." />
          ) : caminho === "aumento" ? (
            <Aumento ano={ano} centroCustoId={centroCustoId} meses={mesesAbertos} onFechar={onFechar} onEnviado={setAviso} />
          ) : (
            <Remanejar ano={ano} centroCustoId={centroCustoId} meses={mesesAbertos} mesesOrigem={mesesTodos}
              onFechar={onFechar} onEnviado={setAviso} />
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Aumento ──────────────────────────────────────────────────────────────────

function Aumento({ ano, centroCustoId, meses, onFechar, onEnviado }: {
  ano: number; centroCustoId: string; meses: number[]; onFechar: () => void; onEnviado: (a: string) => void;
}) {
  const qc = useQueryClient();
  const [mes, setMes] = useState(meses[0]!);
  const [linhaId, setLinhaId] = useState("");
  const [valor, setValor] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");

  // A revisão é pedida sobre a linha de UM mês: as linhas vêm do mês escolhido.
  const linhas = useQuery({
    queryKey: ["linhas-mes", comp(ano, mes), centroCustoId],
    queryFn: () => api.get<{ linhas: LinhaMes[] }>(
      `/api/execucao/linhas?competencia=${comp(ano, mes)}&centro_custo_id=${centroCustoId}&limit=500`),
  });
  const candidatas = (linhas.data?.linhas ?? []).filter((l) => l.linha_id);
  const escolhida = candidatas.find((l) => l.linha_id === linhaId) ?? candidatas[0];
  // ⚠️ O valor é o NOVO valor da linha, não o acréscimo. Achado em 23/09: um
  // pedido de R$ 5.000 para uma linha de R$ 15.000 — quem pediu digitou o
  // acréscimo. A 047 recusa no banco; a tela mostra a conta antes de enviar.
  const atualC = escolhida ? BigInt(escolhida.orcado_centavos) : 0n;
  const pedidoC = valor ? BigInt(valor) : 0n;
  const naoAumenta = !!escolhida && pedidoC > 0n && pedidoC <= atualC;

  const enviar = useMutation({
    mutationFn: () => solicitarRevisao({
      linha_id: escolhida!.linha_id!,
      valor_solicitado_centavos: valor ?? "0",
      motivo: motivo.trim(),
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ predicate: (x) => ["solicitacoes", "contadores", "avisos"].includes(String(x.queryKey[0])) });
      onEnviado(r.aviso ?? "Pedido de aumento enviado ao Financeiro.");
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={rotuloCampo}>Mês do orçamento</span>
          <select value={mes} onChange={(e) => { setMes(Number(e.target.value)); setLinhaId(""); }} className={campo}>
            {meses.map((m) => <option key={m} value={m}>{MESES[m]}/{ano}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={rotuloCampo}>Linha orçamentária</span>
          <select value={escolhida?.linha_id ?? ""} onChange={(e) => setLinhaId(e.target.value)} className={campo}
            disabled={candidatas.length === 0}>
            {candidatas.length === 0 && <option value="">{linhas.isPending ? "Carregando…" : "Nenhuma linha neste mês"}</option>}
            {candidatas.map((l) => (
              <option key={l.linha_id} value={l.linha_id!}>
                {l.categoria_nome}{l.fornecedor_nome ? ` · ${l.fornecedor_nome}` : ""}
              </option>
            ))}
          </select>
        </label>
      </div>
      {escolhida && (
        <p className="num -mt-2 text-[11.5px] text-tinta-fraca">
          hoje: orçado {moeda(escolhida.orcado_centavos)} · realizado {moeda(escolhida.realizado_centavos)} · comprometido {moeda(escolhida.comprometido_centavos)}
        </p>
      )}
      <label className="block">
        <span className={rotuloCampo}>Novo valor da linha no mês</span>
        <CampoDinheiro valorCentavos={valor} onMudar={setValor} placeholder="O valor total que a linha passa a ter" />
      </label>
      {escolhida && pedidoC > 0n && !naoAumenta && (
        <p className="num -mt-2 text-[12px] text-tinta-media">
          De {moeda(atualC.toString())} para <strong>{moeda(pedidoC.toString())}</strong>{" "}
          <span className="text-ok-tinta">(+{moeda((pedidoC - atualC).toString())})</span>
        </p>
      )}
      {naoAumenta && (
        <p className="-mt-2 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] leading-snug text-atencao-tinta">
          O valor é o <strong>novo valor total</strong> da linha no mês, e tem de ser maior que o orçado atual,{" "}
          <span className="num">{moeda(atualC.toString())}</span>. Para pedir mais{" "}
          <span className="num">{moeda(pedidoC.toString())}</span>, digite{" "}
          <span className="num">{moeda((atualC + pedidoC).toString())}</span>. Para reduzir ou mover orçamento,
          use <strong>Remanejar</strong>.
        </p>
      )}
      <label className="block">
        <span className={rotuloCampo}>Justificativa</span>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} className={campo}
          placeholder="O que mudou e por que o teto atual não comporta." />
      </label>
      {enviar.isError && <Erro e={enviar.error} />}
      <Rodape onFechar={onFechar} pendente={enviar.isPending}
        pode={!!escolhida && pedidoC > atualC && motivo.trim().length >= 5}
        rotulo="Enviar pedido de aumento" onEnviar={() => enviar.mutate()} />
    </div>
  );
}

// ── Remanejar ────────────────────────────────────────────────────────────────

function Remanejar({ ano, centroCustoId, meses, mesesOrigem, onFechar, onEnviado }: {
  ano: number; centroCustoId: string; meses: number[]; mesesOrigem: number[];
  onFechar: () => void; onEnviado: (a: string) => void;
}) {
  const qc = useQueryClient();
  const [modo, setModo] = useState<Modo>("linhas");
  const [mes, setMes] = useState(meses[0]!);
  const [deMes, setDeMes] = useState(meses[0]!);
  const [paraMes, setParaMes] = useState(meses[1] ?? meses[0]!);
  const [de, setDe] = useState("");
  const [para, setPara] = useState("");
  const [valor, setValor] = useState<bigint>(0n);
  const [motivo, setMotivo] = useState("");

  const q = useQuery({
    queryKey: ["orcado-realizado", ano, "despesa"],
    queryFn: () => buscarOrcadoRealizado(ano, "despesa"),
  });
  // Categorias que recebem orçamento (v_categoria_orcavel): o destino pode ser
  // uma delas mesmo sem linha no ano — a linha nasce na aprovação (045).
  const cadastros = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<Cadastros>("/api/cadastros") });

  // CC × categoria, com orçado e usado (realizado + comprometido) por mês.
  const cats = useMemo(() => {
    const m = new Map<string, Cat>();
    for (const l of q.data?.linhas ?? []) {
      // Lançamento sem categoria (044) não tem orçado para ceder nem receber.
      if (!l.categoria_id) continue;
      const chave = `${l.centro_custo_id}|${l.categoria_id}`;
      let c = m.get(chave);
      if (!c) {
        c = {
          chave, ccId: l.centro_custo_id, ccRotulo: `${l.centro_custo_codigo} · ${l.centro_custo_nome}`,
          catId: l.categoria_id, catRotulo: `${l.categoria_codigo ? l.categoria_codigo + " " : ""}${l.categoria_nome}`,
          orc: MESES.map(() => 0n), usado: MESES.map(() => 0n),
        };
        m.set(chave, c);
      }
      const i = Number(l.competencia.slice(5, 7)) - 1;
      c.orc[i] = c.orc[i]! + BigInt(l.orcado_centavos);
      c.usado[i] = c.usado[i]! + BigInt(l.realizado_centavos ?? "0") + BigInt(l.comprometido_centavos ?? "0");
    }
    return [...m.values()];
  }, [q.data]);

  const livre = (c: Cat | undefined, i: number) => {
    if (!c) return 0n;
    const s = c.orc[i]! - c.usado[i]!;
    return s > 0n ? s : 0n;
  };
  const livreEm = (c: Cat | undefined, ms: number[]) => ms.reduce((a, i) => a + livre(c, i), 0n);
  // O "Entre meses" é o único em que a origem pode ser mês fechado: nos outros
  // dois o destino usa os mesmos meses, e mês fechado não recebe.
  const podeCeder = modo === "meses" ? mesesOrigem : meses;
  const mesesDe = modo === "linhas" ? [mes] : modo === "meses" ? [deMes] : meses;
  const mesesPara = modo === "linhas" ? [mes] : modo === "meses" ? [paraMes] : meses;

  // ⚠️ Achado no primeiro teste (23/09): a tela abria na primeira categoria da
  // lista, e com ela esgotada mostrava "R$ 0,00 disponível" sem dizer por quê.
  // Agora abre na primeira que TEM saldo livre, e cada opção diz quanto tem.
  const doCc = cats.filter((c) => c.ccId === centroCustoId);
  const origem = doCc.find((c) => c.chave === de) ?? doCc.find((c) => livreEm(c, mesesDe) > 0n) ?? doCc[0];
  // Destino: só o mesmo centro de custo (045), e só categoria que recebe
  // orçamento — com ou sem linha no ano.
  const orcaveis = (cadastros.data?.categorias ?? []).filter((c) => c.natureza !== "receita");
  const idsOrcaveis = new Set(orcaveis.map((c) => c.id));
  const destinos: Cat[] = modo === "meses" ? [] : [
    ...doCc.filter((c) => c.chave !== origem?.chave && idsOrcaveis.has(c.catId)),
    ...orcaveis
      .filter((c) => c.id !== origem?.catId && !doCc.some((x) => x.catId === c.id))
      .map((c) => ({
        chave: `${centroCustoId}|${c.id}`, ccId: centroCustoId, ccRotulo: origem?.ccRotulo ?? "",
        catId: c.id, catRotulo: `${c.codigo ? c.codigo + " " : ""}${c.nome}`,
        orc: MESES.map(() => 0n), usado: MESES.map(() => 0n),
      })),
  ].sort((a, b) => a.catRotulo.localeCompare(b.catRotulo, "pt-BR", { numeric: true }));
  const destino = modo === "meses" ? origem : destinos.find((c) => c.chave === para) ?? destinos[0];
  const disponivel = livreEm(origem, mesesDe);
  // Há saldo livre em alguma categoria deste CC, em algum mês que pode ceder?
  const ccTemSaldo = doCc.some((c) => livreEm(c, podeCeder) > 0n);
  // Meses do destino sem linha orçada: a linha é criada na aprovação.
  const semLinha = (c: Cat | undefined, ms: number[]) => ms.filter((i) => !c || c.orc[i]! === 0n);
  const rotuloLivre = (x: bigint) => (x > 0n ? `livre ${moeda(x.toString())}` : "sem saldo livre");
  const v = valor > disponivel ? disponivel : valor;
  const soma = (c: Cat | undefined, ms: number[], k: "orc" | "usado") => ms.reduce((a, i) => a + (c ? c[k][i]! : 0n), 0n);
  const mesmoMes = modo === "meses" && deMes === paraMes;

  const enviar = useMutation({
    mutationFn: () => solicitarRemanejamento({
      modo,
      origem: { centro_custo_id: origem!.ccId, categoria_id: origem!.catId, competencias: mesesDe.map((i) => comp(ano, i)) },
      destino: { centro_custo_id: destino!.ccId, categoria_id: destino!.catId, competencias: mesesPara.map((i) => comp(ano, i)) },
      valor_centavos: v.toString(),
      motivo: motivo.trim(),
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ predicate: (x) => ["solicitacoes", "contadores", "avisos"].includes(String(x.queryKey[0])) });
      onEnviado(r.aviso);
    },
  });

  if (q.isPending || cadastros.isPending) return <p className="text-[13px] text-tinta-fraca">Carregando o orçamento…</p>;
  if (q.isError) return <Erro e={q.error} />;
  if (cadastros.isError) return <Erro e={cadastros.error} />;
  if (!origem) return <Vazio titulo="Nenhuma linha orçada neste centro de custo" dica={`Não há orçamento de despesa em ${ano} para remanejar.`} />;

  const selectMes = (valorAtual: number, set: (n: number) => void, rotulo: string, anotar?: (m: number) => string,
    lista: number[] = meses) => (
    <label className="block">
      <span className={rotuloCampo}>{rotulo}</span>
      <select value={valorAtual} onChange={(e) => { set(Number(e.target.value)); setValor(0n); }} className={campo}>
        {lista.map((m) => <option key={m} value={m}>{MESES[m]}/{ano}{anotar ? ` · ${anotar(m)}` : ""}</option>)}
      </select>
    </label>
  );
  // No "Entre linhas", o mês diz se alguma categoria do CC tem saldo nele.
  const anotarMesDoCc = (m: number) => {
    const n = doCc.filter((c) => livre(c, m) > 0n).length;
    return n === 0 ? "sem saldo livre" : `${n} ${n === 1 ? "categoria" : "categorias"} com saldo`;
  };
  const passo = 5000n; // R$ 50
  const atalho = (fr: bigint, den: bigint) => setValor(den === 1n ? disponivel : ((disponivel * fr) / den / passo) * passo);

  // A barra: usado (azul), livre (azul claro), o que sai (hachurado) ou entra (verde).
  const barra = (orc: bigint, usado: bigint, delta: bigint, lado: "de" | "para") => {
    const base = Number(orc + (lado === "para" ? delta : 0n)) || 1;
    const u = Math.min(Number(usado), Number(orc)) / base * 100;
    const l = Math.max(Number(orc - usado - (lado === "de" ? delta : 0n)), 0) / base * 100;
    const d = Number(delta) / base * 100;
    return (
      <div className="flex h-2.5 overflow-hidden rounded-full bg-superficie-2">
        <span className="bg-azul-medio" style={{ width: `${u}%` }} />
        <span className="bg-[#9fb1d3]" style={{ width: `${l}%` }} />
        <span style={{
          width: `${d}%`,
          background: lado === "de"
            ? "repeating-linear-gradient(45deg, var(--color-risco-linha) 0 4px, var(--color-risco-bg) 4px 8px)"
            : "var(--color-ok-linha)",
        }} />
      </div>
    );
  };
  const ponta = (lado: "de" | "para", c: Cat | undefined, ms: number[], seletor: ReactNode) => {
    const orc = soma(c, ms, "orc"), usado = soma(c, ms, "usado");
    return (
      <div className="grid content-start gap-2 rounded-xl border border-linha p-3">
        <span className={`text-[10px] font-bold tracking-[0.07em] uppercase ${lado === "de" ? "text-risco-tinta" : "text-ok-tinta"}`}>
          {lado === "de" ? "De" : "Para"}
        </span>
        {seletor}
        {barra(orc, usado, v, lado)}
        <div className="num flex justify-between text-[12px] text-tinta-media">
          <span>orçado {moeda(orc.toString())}</span>
          <strong className="text-[13px] text-tinta">{moeda((lado === "de" ? orc - v : orc + v).toString())}</strong>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div role="group" aria-label="Tipo de remanejamento" className="inline-flex gap-0.5 rounded-lg bg-superficie-2 p-[3px]">
        {([["linhas", "Entre linhas"], ["meses", "Entre meses"], ["ano", "No ano"]] as const).map(([m, r]) => (
          <button key={m} type="button" aria-pressed={modo === m} onClick={() => { setModo(m); setValor(0n); }}
            className={`rounded-md px-3.5 py-1.5 text-[12.5px] font-semibold ${modo === m ? "bg-superficie text-tinta shadow-sm" : "text-tinta-media"}`}>
            {r}
          </button>
        ))}
      </div>

      {modo === "linhas" && selectMes(mes, (m) => { setMes(m); setDe(""); }, "Mês", anotarMesDoCc)}

      <div className="grid items-stretch gap-2.5 sm:grid-cols-[1fr_auto_1fr]">
        {ponta("de", origem, mesesDe, (
          <>
            <select value={origem.chave} onChange={(e) => { setDe(e.target.value); setValor(0n); }} className={campo}>
              {doCc.map((c) => (
                <option key={c.chave} value={c.chave}>{c.catRotulo} · {rotuloLivre(livreEm(c, mesesDe))}</option>
              ))}
            </select>
            {modo === "meses" && selectMes(deMes, setDeMes, "Mês de origem", (m) => rotuloLivre(livre(origem, m)), mesesOrigem)}
            {modo === "ano" && <span className="text-[11.5px] text-tinta-fraca">{MESES[meses[0]!]} a {MESES[meses[meses.length - 1]!]}/{ano}</span>}
          </>
        ))}
        <div aria-hidden className="grid place-items-center text-[22px] font-bold text-laranja max-sm:rotate-90">→</div>
        {ponta("para", destino, mesesPara, modo === "meses" ? (
          <>
            <select disabled className={`${campo} bg-superficie-2 text-tinta-media`}><option>{origem.catRotulo}</option></select>
            {selectMes(paraMes, setParaMes, "Mês de destino", (m) => (origem.orc[m]! === 0n ? "sem linha" : `orçado ${moeda(origem.orc[m]!.toString())}`))}
          </>
        ) : (
          <select value={destino?.chave ?? ""} onChange={(e) => setPara(e.target.value)} className={campo}
            disabled={destinos.length === 0}>
            {destinos.length === 0 && <option value="">Nenhuma outra categoria que receba orçamento</option>}
            {destinos.map((c) => (
              <option key={c.chave} value={c.chave}>
                {c.catRotulo}{semLinha(c, mesesPara).length === mesesPara.length ? " · sem linha" : ""}
              </option>
            ))}
          </select>
        ))}
      </div>

      <div className="grid gap-2.5 rounded-xl border border-linha bg-[#fbfcfe] p-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <strong className="num font-display text-[24px]">{moeda(v.toString())}</strong>
          <span className="num text-[12px] text-tinta-fraca">disponível para remanejar: {moeda(disponivel.toString())}</span>
        </div>
        <input type="range" aria-label="Valor a remanejar" min={0} max={Number(disponivel)} step={Number(passo)}
          value={Number(v)} disabled={disponivel === 0n}
          onChange={(e) => setValor(BigInt(e.target.value))} className="h-6 w-full accent-laranja" />
        <div className="flex flex-wrap items-center gap-1.5">
          {([[1n, 4n, "25%"], [1n, 2n, "50%"], [1n, 1n, "Todo o disponível"]] as const).map(([fr, den, r]) => (
            <button key={r} type="button" onClick={() => atalho(fr, den)} disabled={disponivel === 0n}
              className="rounded-full border border-linha bg-superficie px-2.5 py-1 text-[11.5px] font-semibold text-tinta-media disabled:opacity-45">
              {r}
            </button>
          ))}
          <span className="ml-auto w-40">
            <CampoDinheiro valorCentavos={v === 0n ? null : v.toString()} onMudar={(c) => setValor(c ? BigInt(c) : 0n)} placeholder="Outro valor" />
          </span>
        </div>
        <p className="text-[12px] leading-snug text-tinta-media">
          Só sai o que ainda não foi realizado nem comprometido. O total {modo === "meses" ? "da linha no ano" : "do centro de custo"} não muda.
          {modo === "ano" && " O valor sai de cada mês na proporção do saldo livre dele, e entra no destino nos mesmos meses."}
        </p>
      </div>

      {disponivel === 0n && (
        <p className="rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] leading-snug text-atencao-tinta">
          {ccTemSaldo ? (
            <>
              <strong>{origem.catRotulo}</strong> não tem saldo livre em{" "}
              {mesesDe.length === 1 ? `${MESES[mesesDe[0]!]}/${ano}` : `${MESES[mesesDe[0]!]} a ${MESES[mesesDe[mesesDe.length - 1]!]}/${ano}`}:
              {" "}orçado {moeda(soma(origem, mesesDe, "orc").toString())}, já realizado ou comprometido{" "}
              {moeda(soma(origem, mesesDe, "usado").toString())}. Escolha uma origem marcada com “livre” na lista
              {modo === "linhas" ? " ou outro mês" : ""}.
            </>
          ) : (
            <>
              Nenhuma categoria deste centro de custo tem saldo livre{" "}
              {modo === "meses" ? `em ${ano}` : `de ${MESES[meses[0]!]}/${ano} em diante`}: o que foi
              orçado já foi realizado ou comprometido. Remanejar só move o que sobra — para cobrir um gasto maior,
              use <strong>Solicitar aumento</strong>.
            </>
          )}
        </p>
      )}

      {destino && semLinha(destino, mesesPara).length > 0 && !mesmoMes && (
        <p className="rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] leading-snug text-tinta-media">
          <strong>{destino.catRotulo}</strong> não tem linha orçada em{" "}
          {semLinha(destino, mesesPara).map((i) => `${MESES[i]}/${ano}`).join(", ")}. A linha é criada quando o
          Financeiro aprovar, sem fornecedor nem subcategoria.
        </p>
      )}

      {mesmoMes && (
        <p className="rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
          Origem e destino são o mesmo mês. Escolha outro mês de destino.
        </p>
      )}

      <label className="block">
        <span className={rotuloCampo}>Justificativa</span>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} className={campo}
          placeholder="Por que o valor sai de um lugar e vai para o outro." />
      </label>
      {enviar.isError && <Erro e={enviar.error} />}
      <Rodape onFechar={onFechar} pendente={enviar.isPending}
        pode={v > 0n && !mesmoMes && !!destino && motivo.trim().length >= 5}
        rotulo="Enviar pedido de remanejamento" onEnviar={() => enviar.mutate()} />
    </div>
  );
}

/** `42883`/`42P01`: a função do banco ainda não foi aplicada neste ambiente.
 *  Para quem está na tela, isso não é erro dele — é "ainda não", com saída. */
function Erro({ e }: { e: unknown }) {
  const f = e as FalhaApi;
  const ainda = f.codigo === "42883" || f.codigo === "42P01";
  return (
    <p className={`rounded-lg border px-3 py-2 text-[12.5px] ${ainda ? "border-atencao-linha bg-atencao-bg text-atencao-tinta" : "border-risco-linha bg-risco-bg text-risco-tinta"}`}>
      {ainda
        ? "O remanejamento entra com a próxima atualização do banco. Por enquanto, use Solicitar aumento."
        : f.message}
    </p>
  );
}

function Rodape({ onFechar, pendente, pode, rotulo, onEnviar }: {
  onFechar: () => void; pendente: boolean; pode: boolean; rotulo: string; onEnviar: () => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onFechar} className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media">
        Cancelar
      </button>
      <button type="button" disabled={!pode || pendente} onClick={onEnviar}
        className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
        {pendente ? "Enviando…" : rotulo}
      </button>
    </div>
  );
}
